import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { getIndustrialRiskOverview } from '../services/industrialRiskService';

import { esNumberFormat } from '../../industrialCost/utils/numberFormat';
type SummaryGlobal = {
  total_kg?: number;
  n_lots?: number;
  avg_price_weighted_eur_per_kg?: number;
};

type SupplierRow = {
  supplier: string;
  total_kg?: number;
  baseline_price?: number;
  verdict?: string;
  mean?: number;
  adverse?: number;
  favorable?: number;
  prob_loss?: number;
};

function n(x: any): number | undefined {
  return typeof x === 'number' && Number.isFinite(x) ? x : undefined;
}

function eurKg(x?: number): string {
  return x == null ? '—' : `${x.toFixed(2)} €/kg`;
}

function fmtKg(x?: number): string {
  return x == null ? '—' : esNumberFormat({ maximumFractionDigits: 0 }).format(x);
}

function pct01(x?: number): string {
  return x == null ? '—' : `${(x * 100).toFixed(1)}%`;
}

function extractSummary(data: any): { global?: SummaryGlobal; rows: SupplierRow[] } {
  const g = data?.global;
  const global =
    g && typeof g === 'object'
      ? {
          total_kg: n(g.total_kg),
          n_lots: n(g.n_lots),
          avg_price_weighted_eur_per_kg: n(g.avg_price_weighted_eur_per_kg),
        }
      : undefined;

  const rows: SupplierRow[] = Array.isArray(data?.suppliers)
    ? data.suppliers
        .map((x: any) => {
          const meta = x?.meta || {};
          const ms = x?.margin_status || {};
          const pk = ms?.profit_per_kg || {};

          return {
            supplier: String(x?.supplier ?? ''),
            total_kg: n(meta?.total_kg),
            baseline_price: n(x?.baseline_price_eur_per_kg),
            verdict: typeof ms?.verdict === 'string' ? ms.verdict : undefined,
            mean: n(pk?.mean),
            adverse: n(pk?.adverse),
            favorable: n(pk?.favorable),
            prob_loss: n(pk?.probability_of_loss),
          };
        })
        .filter((r: SupplierRow) => r.supplier)
    : [];

  return { global, rows };
}

function verdictColor(verdict?: string): 'success' | 'warning' | 'error' | 'default' {
  const v = (verdict || '').toUpperCase();
  if (v === 'ROBUSTO') return 'success';
  if (v === 'INESTABLE') return 'warning';
  if (v === 'CRITICO' || v === 'CRÍTICO' || v === 'NEGATIVO') return 'error';
  return 'default';
}

function computeBusinessState(rows: SupplierRow[]): {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
  narrative: string;
} {
  if (!rows.length) {
    return {
      label: 'Sin base suficiente',
      tone: 'default',
      narrative: 'Todavía no hay información suficiente para construir una lectura ejecutiva del año.',
    };
  }

  const robust = rows.filter((r) => (r.verdict || '').toUpperCase() === 'ROBUSTO').length;
  const unstable = rows.filter((r) => (r.verdict || '').toUpperCase() === 'INESTABLE').length;
  const critical = rows.filter((r) => {
    const v = (r.verdict || '').toUpperCase();
    return v === 'CRITICO' || v === 'CRÍTICO' || v === 'NEGATIVO';
  }).length;

  const withMean = rows.filter((r) => r.mean != null);
  const avgMean =
    withMean.length > 0 ? withMean.reduce((acc, r) => acc + (r.mean || 0), 0) / withMean.length : undefined;

  const topVolume = [...rows]
    .filter((r) => r.total_kg != null)
    .sort((a, b) => (b.total_kg || 0) - (a.total_kg || 0))[0];

  if (critical === 0 && robust >= Math.max(1, rows.length - unstable)) {
    return {
      label: 'Robusto',
      tone: 'success',
      narrative: topVolume?.supplier
        ? `2025 muestra una base razonablemente sólida. ${topVolume.supplier} concentra el mayor volumen y el conjunto de proveedores presenta una lectura económica defendible.`
        : '2025 muestra una base razonablemente sólida y una lectura económica defendible en el conjunto de proveedores.',
    };
  }

  if ((avgMean != null && avgMean > 0) || unstable > 0) {
    return {
      label: 'Desigual',
      tone: 'warning',
      narrative:
        'El año mantiene señales aprovechables, pero no todos los proveedores ofrecen la misma estabilidad. El promedio sirve como referencia, aunque conviene bajar al detalle antes de decidir.',
    };
  }

  return {
    label: 'Bajo revisión',
    tone: 'error',
    narrative:
      'La lectura del año es exigente: el comportamiento observado de los proveedores invita a revisar con más detalle antes de extraer conclusiones ejecutivas.',
  };
}

function findHighlights(rows: SupplierRow[]) {
  const topVolume = [...rows]
    .filter((r) => r.total_kg != null)
    .sort((a, b) => (b.total_kg || 0) - (a.total_kg || 0))[0];

  const bestMargin = [...rows]
    .filter((r) => r.mean != null)
    .sort((a, b) => (b.mean || 0) - (a.mean || 0))[0];

  const highestRisk = [...rows]
    .filter((r) => r.prob_loss != null)
    .sort((a, b) => (b.prob_loss || 0) - (a.prob_loss || 0))[0];

  return { topVolume, bestMargin, highestRisk };
}

export default function ExecutiveSummary2025View() {
  const [raw, setRaw] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summary = useMemo(() => extractSummary(raw as any), [raw]);
  const rows = summary.rows;
  const global = summary.global;

  const businessState = useMemo(() => computeBusinessState(rows), [rows]);
  const highlights = useMemo(() => findHighlights(rows), [rows]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getIndustrialRiskOverview({
          risk_target: 0.2,
          n_sims: 1000,
          max_suppliers: 10,
          baseline: 'weighted_avg',
          price_min_factor: 0.5,
        });
        if (!cancelled) setRaw(data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || 'No se pudo cargar el resumen 2025.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 2.4,
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack spacing={1.1}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.6 }}>
                Resumen 2025
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.82, mt: 0.5 }}>
                Lectura ejecutiva del negocio industrial: volumen comprado, estructura de proveedores y resultado observado.
              </Typography>
            </Box>

            <Chip
              label={businessState.label}
              color={businessState.tone}
              variant="filled"
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          <Typography variant="body1" sx={{ opacity: 0.92 }}>
            {businessState.narrative}
          </Typography>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="inherit" />
        </Box>
      ) : null}

      {!loading && !error ? (
        <>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Kg comprados
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {fmtKg(global?.total_kg)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Volumen total observado en el periodo.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Precio medio de compra
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {eurKg(global?.avg_price_weighted_eur_per_kg)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Referencia ponderada del coste de entrada.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Proveedores activos
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {rows.length || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Proveedores con histórico utilizable en el periodo.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Lectura general del negocio
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {businessState.label}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Síntesis ejecutiva construida sobre el comportamiento observado de los proveedores.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  Mayor volumen
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {highlights.topVolume?.supplier || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {highlights.topVolume?.total_kg != null
                    ? `${fmtKg(highlights.topVolume.total_kg)} kg comprados`
                    : '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  Mejor margen esperado
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {highlights.bestMargin?.supplier || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {highlights.bestMargin?.mean != null
                    ? `${highlights.bestMargin.mean.toFixed(2)} €/kg comprados`
                    : '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  Mayor tensión observada
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {highlights.highestRisk?.supplier || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {highlights.highestRisk?.prob_loss != null
                    ? `${pct01(highlights.highestRisk.prob_loss)} de riesgo de pérdida`
                    : '—'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Proveedores principales
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                  Comparativa ejecutiva de volumen, precio de compra y resultado observado.
                </Typography>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                      Proveedor
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Kg comprados
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Precio medio
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Margen esperado
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Riesgo de pérdida
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Estado
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(0, 8).map((row) => (
                    <TableRow key={row.supplier}>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                        {row.supplier}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {fmtKg(row.total_kg)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {eurKg(row.baseline_price)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {row.mean != null ? `${row.mean.toFixed(2)} €/kg` : '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {pct01(row.prob_loss)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)' }} align="right">
                        <Chip
                          label={row.verdict || 'Sin lectura'}
                          color={verdictColor(row.verdict)}
                          size="small"
                          sx={{ fontWeight: 800 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}

                  {!rows.length ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>
                        No hay proveedores suficientes para construir el resumen.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>

              <Typography variant="caption" sx={{ opacity: 0.65 }}>
                Entorno de trabajo orientado a lectura ejecutiva. Si el dataset sigue en sandbox o cobertura parcial, esta vista debe interpretarse como guía estructural y no como cierre final de negocio.
              </Typography>
            </Stack>
          </Box>
        </>
      ) : null}
    </Stack>
  );
}